/**
 * useFeedSync — Real-time Firestore listener for the Social Feed
 * 
 * Subscribes to scoped project feed, feedComments, and feedReactions subcollections
 * and pushes incoming data into the Zustand FeedSlice.
 * 
 * Guards: Only subscribes when cloud.enabled, cloud.orgId, cloud.projectId, and
 * auth.initialized are all truthy — same pattern as useSync.ts.
 */

import { useEffect, useRef } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { subscribeToPosts, subscribeToComments, subscribeToReactions } from '../feed-api';
import type { FeedComment, FeedPost, FeedReaction } from '../types';
import {
    clearPendingFeedCommentWrite,
    clearPendingFeedPostWrite,
    clearPendingFeedReactionWrite,
    clearPendingFeedWrites,
    isPendingFeedCommentWrite,
    isPendingFeedPostWrite,
    isPendingFeedReactionWrite,
} from '../../sync/feed-pending-writes';
import type { ProjectScope } from '../../sync/project-scope';
import {
    deriveInitialFeedReadReceipt,
    feedReadReceiptKey,
    getFeedReadReceipt,
} from '../feed-read-receipts';
import type { FeedReadReceiptScope } from '../feed-read-receipts';

function normalizePendingComparisonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalizePendingComparisonValue);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([, entryValue]) => entryValue !== undefined)
                .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
                .map(([key, entryValue]) => [key, normalizePendingComparisonValue(entryValue)])
        );
    }
    return value;
}

function recordsMatchForPendingWrite(left: unknown, right: unknown): boolean {
    return JSON.stringify(normalizePendingComparisonValue(left))
        === JSON.stringify(normalizePendingComparisonValue(right));
}

function feedPostForPendingComparison(post: FeedPost): Omit<FeedPost, 'updatedAt'> {
    const { updatedAt: _updatedAt, ...rest } = post;
    return rest;
}

function feedReactionForPendingComparison(reaction: FeedReaction): FeedReaction {
    const {
        updatedAtMs: _updatedAtMs,
        updatedBy: _updatedBy,
        ...rest
    } = reaction as FeedReaction & { updatedAtMs?: unknown; updatedBy?: unknown };
    return rest;
}

function mergePendingLocalById<T extends { id: string }>(
    scope: ProjectScope,
    remoteItems: T[],
    localItems: T[],
    isPendingWrite: (scope: ProjectScope, id: string) => boolean
): T[] {
    const remoteIds = new Set(remoteItems.map(item => item.id));
    const localById = new Map(localItems.map(item => [item.id, item]));
    const mergedRemoteItems = remoteItems.map(item => {
        const localItem = localById.get(item.id);
        if (localItem && isPendingWrite(scope, item.id)) {
            return localItem;
        }
        return item;
    });
    const pendingLocalItems = localItems.filter(item => (
        !remoteIds.has(item.id) && isPendingWrite(scope, item.id)
    ));

    return [...mergedRemoteItems, ...pendingLocalItems];
}

export function mergeFeedPostsWithPendingLocal(
    scope: ProjectScope,
    remotePosts: FeedPost[],
    localPosts: FeedPost[]
): FeedPost[] {
    return mergePendingLocalById(scope, remotePosts, localPosts, isPendingFeedPostWrite)
        .toSorted((left, right) => right.createdAt - left.createdAt);
}

export function mergeFeedCommentsWithPendingLocal(
    scope: ProjectScope,
    remoteComments: FeedComment[],
    localComments: FeedComment[]
): FeedComment[] {
    return mergePendingLocalById(scope, remoteComments, localComments, isPendingFeedCommentWrite)
        .toSorted((left, right) => left.createdAt - right.createdAt);
}

export function mergeFeedReactionsWithPendingLocal(
    scope: ProjectScope,
    remoteReactions: FeedReaction[],
    localReactions: FeedReaction[]
): FeedReaction[] {
    return mergePendingLocalById(scope, remoteReactions, localReactions, isPendingFeedReactionWrite)
        .toSorted((left, right) => right.updatedAt - left.updatedAt);
}

function clearAcknowledgedFeedPostWrites(
    scope: ProjectScope,
    remotePosts: FeedPost[],
    localPosts: FeedPost[]
): void {
    const localById = new Map(localPosts.map(post => [post.id, post]));
    for (const post of remotePosts) {
        const localPost = localById.get(post.id);
        if (
            !localPost
            || recordsMatchForPendingWrite(
                feedPostForPendingComparison(post),
                feedPostForPendingComparison(localPost)
            )
        ) {
            clearPendingFeedPostWrite(scope, post.id);
        }
    }
}

function clearAcknowledgedFeedCommentWrites(
    scope: ProjectScope,
    remoteComments: FeedComment[],
    localComments: FeedComment[]
): void {
    const localById = new Map(localComments.map(comment => [comment.id, comment]));
    for (const comment of remoteComments) {
        const localComment = localById.get(comment.id);
        if (!localComment || recordsMatchForPendingWrite(comment, localComment)) {
            clearPendingFeedCommentWrite(scope, comment.id);
        }
    }
}

export function clearAcknowledgedFeedReactionWrites(
    scope: ProjectScope,
    remoteReactions: FeedReaction[],
    localReactions: FeedReaction[]
): void {
    const localById = new Map(localReactions.map(reaction => [reaction.id, reaction]));
    for (const reaction of remoteReactions) {
        const localReaction = localById.get(reaction.id);
        if (
            !localReaction
            || recordsMatchForPendingWrite(
                feedReactionForPendingComparison(reaction),
                feedReactionForPendingComparison(localReaction)
            )
        ) {
            clearPendingFeedReactionWrite(scope, reaction.id);
        }
    }
}

function deriveFeedReadReceiptState(
    scope: FeedReadReceiptScope,
    posts: FeedPost[],
    receipts: Record<string, number> | undefined
): { lastViewedFeedAt: number; lastViewedFeedAtByScope: Record<string, number> } {
    const existingReceipt = getFeedReadReceipt(receipts, scope);
    const lastViewedFeedAt = existingReceipt ?? deriveInitialFeedReadReceipt(posts);

    if (existingReceipt !== null) {
        return {
            lastViewedFeedAt,
            lastViewedFeedAtByScope: receipts ?? {},
        };
    }

    return {
        lastViewedFeedAt,
        lastViewedFeedAtByScope: {
            ...(receipts ?? {}),
            [feedReadReceiptKey(scope)]: lastViewedFeedAt,
        },
    };
}

export function useFeedSync(enabled = true) {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);
    const user = useAppStore(state => state.auth.user);

    const postUnsubRef = useRef<(() => void) | null>(null);
    const commentUnsubRef = useRef<(() => void) | null>(null);
    const reactionUnsubRef = useRef<(() => void) | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        // Tear down previous subscriptions on any dependency change
        if (postUnsubRef.current) {
            postUnsubRef.current();
            postUnsubRef.current = null;
        }
        if (commentUnsubRef.current) {
            commentUnsubRef.current();
            commentUnsubRef.current = null;
        }
        if (reactionUnsubRef.current) {
            reactionUnsubRef.current();
            reactionUnsubRef.current = null;
        }

        // Guard: Only subscribe when all conditions are met
        if (!enabled || !cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user) {
            return;
        }

        const scope = { orgId, projectId };
        const readReceiptScope = { ...scope, userId: user.uid };
        clearPendingFeedWrites();

        // Subscribe to posts
        postUnsubRef.current = subscribeToPosts(scope, (posts) => {
            if (!mountedRef.current) return;
            const state = useAppStore.getState();
            const mergedPosts = mergeFeedPostsWithPendingLocal(scope, posts, state.feedPosts || []);
            state.feedActions.setPostsWithReadReceipt(
                mergedPosts,
                deriveFeedReadReceiptState(
                    readReceiptScope,
                    posts,
                    state.lastViewedFeedAtByScope
                )
            );
            clearAcknowledgedFeedPostWrites(scope, posts, useAppStore.getState().feedPosts || []);
        });

        // Subscribe to comments
        commentUnsubRef.current = subscribeToComments(scope, (comments) => {
            if (!mountedRef.current) return;
            const state = useAppStore.getState();
            state.feedActions.setComments(
                mergeFeedCommentsWithPendingLocal(scope, comments, state.feedComments || [])
            );
            clearAcknowledgedFeedCommentWrites(scope, comments, useAppStore.getState().feedComments || []);
        });

        // Subscribe to reactions
        reactionUnsubRef.current = subscribeToReactions(scope, (reactions) => {
            if (!mountedRef.current) return;
            const state = useAppStore.getState();
            state.feedActions.setReactions(
                mergeFeedReactionsWithPendingLocal(scope, reactions, state.feedReactions || [])
            );
            clearAcknowledgedFeedReactionWrites(scope, reactions, useAppStore.getState().feedReactions || []);
        });

        return () => {
            if (postUnsubRef.current) {
                postUnsubRef.current();
                postUnsubRef.current = null;
            }
            if (commentUnsubRef.current) {
                commentUnsubRef.current();
                commentUnsubRef.current = null;
            }
            if (reactionUnsubRef.current) {
                reactionUnsubRef.current();
                reactionUnsubRef.current = null;
            }
        };
    }, [enabled, cloudEnabled, orgId, projectId, isAuthInitialized, user]);
}
